"use client";

import type React from "react";
import { useEffect, useRef } from "react";

interface Point {
  x: number;
  y: number;
  label?: string;
}

interface VideoSectionProps {
  imageSrc: string;
  points: Point[];
  studioName: string;
  projectTitle: string;
  className?: string;
  priority?: boolean;
}

const VideoSection: React.FC<VideoSectionProps> = ({
  imageSrc,
  points,
  studioName,
  projectTitle,
  className = "",
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    const container = containerRef.current;
    if (!ctx || !canvas || !container) return;

    let isMounted = true;
    const imgElement = new window.Image();

    const updateCanvas = () => {
      if (!isMounted || !ctx || !canvas || !container) return;
      
      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;

      const dpr = window.devicePixelRatio || 1;
      canvas.width = containerWidth * dpr;
      canvas.height = containerHeight * dpr;
      canvas.style.width = `${containerWidth}px`;
      canvas.style.height = `${containerHeight}px`;

      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, containerWidth, containerHeight);

      const imgAspect = imgElement.width / imgElement.height;
      const containerAspect = containerWidth / containerHeight;
      
      let drawWidth, drawHeight, offsetX, offsetY;
      
      if (imgAspect > containerAspect) {
        drawHeight = containerHeight;
        drawWidth = containerHeight * imgAspect;
        offsetX = (containerWidth - drawWidth) / 2;
        offsetY = 0;
      } else {
        drawWidth = containerWidth;
        drawHeight = containerWidth / imgAspect;
        offsetX = 0;
        offsetY = (containerHeight - drawHeight) / 2;
      }
      
      ctx.drawImage(imgElement, offsetX, offsetY, drawWidth, drawHeight);

      // Draw points
      // Predefined neon hues for more vibrant colors (matches playground2)
      const NEON_HUES = [100, 180, 320, 60, 270, 30, 200, 140]; // Start with lime

      points.forEach((point, idx) => {
        const x = offsetX + (point.x * drawWidth);
        const y = offsetY + (point.y * drawHeight);

        // Assign color from NEON_HUES array (cycling through if more points than colors)
        const hue = NEON_HUES[idx % NEON_HUES.length];
        const pointColor = `hsl(${hue}, 100%, 65%)`;
        const transparentColor = `hsla(${hue}, 100%, 65%, 0.3)`;
        
        const outerRadius = 12;
        const middleRadius = 12;
        const innerRadius = 3;

        ctx.beginPath();
        ctx.arc(x, y, outerRadius, 0, 2 * Math.PI);
        ctx.strokeStyle = pointColor;
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(x, y, middleRadius, 0, 2 * Math.PI);
        ctx.fillStyle = transparentColor;
        ctx.fill();

        ctx.beginPath();
        ctx.arc(x, y, innerRadius, 0, 2 * Math.PI);
        ctx.fillStyle = pointColor;
        ctx.fill();

        // Draw label if present
        if (point.label) {
          ctx.font = "9px system-ui, -apple-system, sans-serif";
          const textMetrics = ctx.measureText(point.label);
          const textWidth = textMetrics.width;
          const textHeight = 9;
          const padding = 6;
          const borderRadius = 4;
          
          // Position label below the point, closer
          const labelX = x - (textWidth + padding * 2) / 2;
          const labelY = y + outerRadius + 4;
          const bgHeight = textHeight + padding * 2;
          
          // Draw rounded rectangle background
          ctx.fillStyle = pointColor;
          ctx.beginPath();
          ctx.roundRect(labelX, labelY, textWidth + padding * 2, bgHeight, borderRadius);
          ctx.fill();
          
          // Draw label text (vertically centered in background)
          ctx.fillStyle = "black";
          ctx.textBaseline = "middle";
          ctx.fillText(point.label, labelX + padding, labelY + bgHeight / 2);
          ctx.textBaseline = "alphabetic"; // Reset to default
        }
      });
    };

    imgElement.onload = () => {
      if (!isMounted) return;
      updateCanvas();
    };

    imgElement.src = imageSrc;

    const resizeObserver = new ResizeObserver(() => {
      if (imgElement.complete) {
        updateCanvas();
      }
    });

    if (container) {
      resizeObserver.observe(container);
    }

    return () => {
      isMounted = false;
      resizeObserver.disconnect();
    };
  }, [imageSrc, points]);

  return (
    <div
      ref={containerRef}
      className={`group relative w-full overflow-hidden rounded-lg ${className}`}
    >
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
      />

      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />

      <div className="absolute inset-0 bottom-6 flex items-end">
        <div className="px-4 text-left text-white sm:px-5 md:px-6 lg:px-8">
          <p className="mb-1 text-xs font-medium uppercase tracking-wider opacity-80 sm:text-sm md:text-base">
            {studioName}
          </p>
          <h2 className="max-w-md text-2xl font-light tracking-tight sm:text-3xl md:text-4xl lg:max-w-lg" style={{ textShadow: '0 2px 8px rgba(0, 0, 0, 0.3)' }}>
            {projectTitle}
          </h2>
        </div>
      </div>
    </div>
  );
};

const Demos: React.FC = () => {
  const demoSections = [
    {
      imageSrc: "/images/solutions/computer-use/login_ui.jpeg",
      points: [
        {
          x: 0.37927663734115347,
          y: 0.5992179863147605,
          label: "checkbox"
        }
      ],
      studioName: "Use Case",
      projectTitle: "UI automation",
    },
    {
      imageSrc: "/images/pick_and_place.jpeg",
      points: [
        {
          x: 0.6295210166177908,
          y: 0.33528836754643204,
          label: "bolt bag"
        },
        {
          x: 0.7165200391006843,
          y: 0.33528836754643204,
          label: "bolt bag"
        },
        {
          x: 0.8113391984359726,
          y: 0.33528836754643204,
          label: "bolt bag"
        }
      ],
      studioName: "Use Case",
      projectTitle: "Robotics",
    },
    {
      imageSrc: "/images/solutions/identify-defects/hotspot_example.jpeg",
      points: [
        {
          x: 0.5738025415444771,
          y: 0.5004887585532747,
          label: "hotspot"
        }
      ],
      studioName: "Use Case",
      projectTitle: "Industrial",
    },
    {
      imageSrc: "/images/solutions/identify-defects/paint-chips.jpeg",
      points: [
        {
          x: 0.44086021505376344,
          y: 0.6666666666666666,
          label: "paint chip"
        },
        {
          x: 0.44281524926686217,
          y: 0.5943304007820137,
          label: "paint chip"
        },
        {
          x: 0.46432062561094817,
          y: 0.5698924731182796,
          label: "paint chip"
        },
        {
          x: 0.3010752688172043,
          y: 0.34506353861192574,
          label: "paint chip"
        },
        {
          x: 0.8592375366568915,
          y: 0.5601173020527859,
          label: "paint chip"
        },
        {
          x: 0.696969696969697,
          y: 0.3118279569892473,
          label: "paint chip"
        },
        {
          x: 0.6402737047898338,
          y: 0.3919843597262952,
          label: "paint chip"
        },
        {
          x: 0.5102639296187683,
          y: 0.07135874877810362,
          label: "paint chip"
        },
        {
          x: 0.19452590420332355,
          y: 0.11827956989247312,
          label: "paint chip"
        },
        {
          x: 0.14076246334310852,
          y: 0.4887585532746823,
          label: "paint chip"
        },
        {
          x: 0.23460410557184752,
          y: 0.375366568914956,
          label: "paint chip"
        }
      ],
      studioName: "Use Case",
      projectTitle: "Damage detection",
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
      {demoSections.map((section, index) => (
        <VideoSection
          key={index}
          imageSrc={section.imageSrc}
          points={section.points}
          studioName={section.studioName}
          projectTitle={section.projectTitle}
          className="aspect-video"
        />
      ))}
    </div>
  );
};

export { Demos };


